import { useMemo, useState, useEffect, useRef } from 'preact/hooks'
import cryptoRandomString from 'crypto-random-string'

import { ControllerAttributes, PricehelperResponseData } from './types.ts'

import { PRODUCT_VIEW_SCRIPT_ID, generateGtmScriptNode } from './generateGtmScriptNode.ts'

declare global {
  interface Window {
    walley: any
  }
}

interface PartPaymentParameters {
  token: string
  url: string
  maxPayment: number
  minPayment: number
}

// Component values the payload was built from, kept with the response for add to cart
interface PricehelperRequest {
  configResult?: string
  quantityFactor: number
  addons: Array<{ scensumArticleId: string; erpItemNumber: string; quantity: number }>
  response: Promise<PricehelperResponseData | undefined>
}

// Survives DOM detach/reattach (module not re-executed), resets on full page navigation
let persistedQuantityFactor = 1

const ProductPriceBlockController = ({
  articleErpItemNumber,
  channelId,
  category,
  subCategory,
  weeksUntilDelivery,
  configResult,
  awaitConfigResultBeforePricehelperRequest,
  addons: addonsJSONstr,
  title,
  titleLong,
}: ControllerAttributes) => {
  const ProductPriceBlock: HTMLElement | null = document.getElementById('productPriceBlock')
  const controllerElement: HTMLElement | null = document.getElementById(
    'productPriceBlockController'
  )
  const baseUrl = controllerElement?.getAttribute('scensum-base-url') || null

  if (!baseUrl) throw new Error(`Unsuccessful attempt to fetch baseUrl for channel ${channelId}`)

  const isValidJson = (jsonString: string) => {
    try {
      JSON.parse(jsonString)
      return true
    } catch (e) {
      return false
    }
  }

  const addons = useMemo(() => (addonsJSONstr ? JSON.parse(addonsJSONstr) : []), [addonsJSONstr])

  const [PricehelperResponseData, setPricehelperResponseData] = useState<
    PricehelperResponseData | undefined
  >(undefined)
  const [quantityFactor, setQuantityFactor] = useState<number>(persistedQuantityFactor)
  const [partPaymentParameters, setPartPaymentParameters] = useState<PartPaymentParameters | null>(
    null
  )
  const [disabledInPim, setDisabledInPim] = useState<boolean>(false)
  const [disabledInLfc, setDisabledInLfc] = useState<boolean>(false)

  const latestRequestRef = useRef<PricehelperRequest | null>(null)

  const fetchPriceAndAvailability = async (
    payload: {
      articles: { qty: Number; sku: string | never[] }[]
    },
    snapshot: Omit<PricehelperRequest, 'response'>
  ) => {
    const pricehelperResponse = fetch(`${baseUrl}/api/v1/pricehelper`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        return response.json()
      })
      .catch((error) => console.error('Fetch error:', error))

    const request: PricehelperRequest = { ...snapshot, response: pricehelperResponse }
    latestRequestRef.current = request

    const response = await pricehelperResponse

    // Responses may arrive out of order, only the latest started request may update state
    if (latestRequestRef.current !== request) return

    setPricehelperResponseData(response)
  }

  useEffect(() => {
    try {
      if (ProductPriceBlock) {
        ProductPriceBlock?.setAttribute(
          'article-numbers',
          articleErpItemNumber
            .split(' & ')
            .concat(
              addons.length
                ? addons.map(
                    (addon: {
                      scensumArticleId: string
                      erpItemNumber: string
                      quantity: number
                    }) => addon.erpItemNumber
                  )
                : []
            )
            .join(', ')
        )
      } else {
        throw new Error(
          'This controller component requires the product-price-block web component set up with id ProductPriceBlock'
        )
      }
    } catch (error) {
      console.error(error)
    }
  }, [ProductPriceBlock, articleErpItemNumber, addons])

  useEffect(() => {
    try {
      if (awaitConfigResultBeforePricehelperRequest && !configResult) {
        return
      }

      const articlesForPriceAndAvailabilityRequest =
        configResult && isValidJson(configResult)
          ? JSON.parse(configResult).bom.flatMap(
              (bomSection: { articles: any[] }) => bomSection.articles
            )
          : articleErpItemNumber.split(' & ').map((articleId: string) => ({ articleId }))

      const payload = articlesForPriceAndAvailabilityRequest
        .map(
          (article: {
            articleId: string
            quantity: number | undefined
            pricingInfo: string | undefined
          }) => ({
            sku: article.articleId,
            qty: (article.quantity ?? 1) * quantityFactor,
            ...(article.pricingInfo && { overridePricing: article.pricingInfo }),
          })
        )
        .concat(
          addons.length
            ? addons.map(
                (addon: { scensumArticleId: string; erpItemNumber: string; quantity: number }) => ({
                  sku: addon.erpItemNumber,
                  qty: addon.quantity,
                })
              )
            : []
        )

      fetchPriceAndAvailability({ articles: payload }, { configResult, quantityFactor, addons })
    } catch (error) {
      console.error('Error in ProductPriceBlockController:', error)
    }
  }, [
    articleErpItemNumber,
    quantityFactor,
    configResult,
    awaitConfigResultBeforePricehelperRequest,
    addons,
  ])

  useEffect(() => {
    const handleQuantityChange = async (event: Event) => {
      if (event instanceof CustomEvent && event.detail && event.detail.qty) {
        persistedQuantityFactor = event.detail.qty
        setQuantityFactor(event.detail.qty)
      }
    }

    ;(document as EventTarget).addEventListener(
      'product-price-block-quantity-change',
      handleQuantityChange
    )

    return () => {
      ;(document as EventTarget).removeEventListener(
        'product-price-block-quantity-change',
        handleQuantityChange
      )
    }
  }, [])

  const addToCart = async () => {
    const request = latestRequestRef.current
    const responseData = await request?.response

    if (!request || !responseData?.articles) {
      console.error('Articles are missing for addtocart request.')
      return
    }

    // Shadows the props on purpose: the cart is built from the snapshot of the awaited request
    const { configResult, quantityFactor, addons } = request

    const handleCartUpdate = () => {
      const ProductPriceBlock: HTMLElement | null = document.getElementById('productPriceBlock')

      if (!ProductPriceBlock) {
        throw Error('Cannot find element with id ProductPriceBlock')
      }

      const event = new CustomEvent('product-price-block-updated-cart', {
        bubbles: true,
        cancelable: true,
        detail: {
          // articleId: (always omitted atm, provided by Scensum BE rendered HTML)
          ...(addons.length && {
            addonsIds: addons.map(
              (addon: { scensumArticleId: string; erpItemNumber: string; quantity: number }) =>
                addon.scensumArticleId
            ),
          }),
        },
      })

      ProductPriceBlock.dispatchEvent(event)
    }

    const withConfigResult = configResult && isValidJson(configResult)
    const parsedConfiguration = withConfigResult ? JSON.parse(configResult) : null

    fetch(`${baseUrl}/cart${withConfigResult ? '/addconfigurationsections' : '/addjson'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        withConfigResult
          ? {
              ConfigurationId:
                parsedConfiguration.id ?? `G_${cryptoRandomString({ length: 10 })}`,
              ConfigurationType: parsedConfiguration.type,
              ...((parsedConfiguration.meta?.title ||
                parsedConfiguration.meta?.Title ||
                parsedConfiguration.meta?.titleLong ||
                parsedConfiguration.meta?.TitleLong) && { Meta: parsedConfiguration.meta }),
              ConfigurationSections: parsedConfiguration.bom
                .map((bomSection: { articles: any[] }) => ({
                  ...bomSection,
                  articles: bomSection.articles.map((art) => ({
                    Sku: art.articleId,
                    Quantity: art.quantity * quantityFactor,
                    ...(art.type && { Type: art.type }),
                    ...(art.contextId && { ContextId: art.contextId }),
                    ...(art.chosenValues && { ChosenValues: art.chosenValues }),
                    ...(art.configString && { ConfigString: art.configString }),
                    ...(art.orderRowDescription && {
                      OrderRowDescription: art.orderRowDescription,
                    }),
                    ...(art.pricingInfo && { PricingInfo: art.pricingInfo }),
                    ...(art.weightVolumeDimensions && {
                      WeightVolumeDimensions: art.weightVolumeDimensions,
                    }),
                    ...(art.editorial && { Editorial: art.editorial }),
                  })),
                }))
                .concat(
                  addons.length
                    ? addons.map(
                        (addon: {
                          scensumArticleId: string
                          erpItemNumber: string
                          quantity: number
                        }) => ({
                          Type: 'ADDON',
                          Position: ((parsedConfiguration.bom.length + 1) * 10).toString(),
                          OwnerPosition: 10,
                          Articles: [
                            {
                              Sku: addon.erpItemNumber,
                              Quantity: addon.quantity,
                            },
                          ],
                        })
                      )
                    : []
                ),
            }
          : responseData.articles
      ),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`) // TODO: [WEBBUTV-5640] Lägg till felhantering i UI
        }

        handleCartUpdate()
      })
      .catch((error) => {
        console.error('Fetch error:', error)

        ProductPriceBlock?.setAttribute('failed-add-to-cart-request', JSON.stringify(error))
      })
  }

  useEffect(() => {
    ProductPriceBlock?.setAttribute(
      'articles',
      JSON.stringify(PricehelperResponseData?.articles ?? [])
    )

    const updatedProductPriceBlock = document.getElementById('productPriceBlock')
    updatedProductPriceBlock?.addEventListener(
      'product-price-block-call-to-action-button-click',
      addToCart
    )

    return () => {
      updatedProductPriceBlock?.removeEventListener(
        'product-price-block-call-to-action-button-click',
        addToCart
      )
    }
  }, [PricehelperResponseData?.articles])

  useEffect(() => {
    ProductPriceBlock?.setAttribute(
      'formated-price',
      PricehelperResponseData?.summary?.FormatedPrice || ''
    )
    ProductPriceBlock?.setAttribute(
      'formated-product-discount',
      PricehelperResponseData?.summary?.FormatedProductDiscount || ''
    )
    ProductPriceBlock?.setAttribute(
      'ordinary-price',
      PricehelperResponseData?.summary?.FormatedOrdinaryPrice?.toString() || ''
    )
    ProductPriceBlock?.setAttribute(
      'price',
      PricehelperResponseData?.summary?.Price?.toString() || ''
    )
    ProductPriceBlock?.setAttribute(
      'product-discount',
      PricehelperResponseData?.summary?.ProductDiscount?.toString() || ''
    )
    ProductPriceBlock?.setAttribute(
      'discounts',
      JSON.stringify(PricehelperResponseData?.summary?.Discounts) || ''
    )
    ProductPriceBlock?.setAttribute(
      'delivery-information',
      PricehelperResponseData?.summary?.DeliveryInformation || ''
    )
    ProductPriceBlock?.setAttribute(
      'delivery-status',
      PricehelperResponseData?.summary?.DeliveryStatus || ''
    )

    if (PricehelperResponseData?.summary?.IsAvailableForPurchase !== undefined) {
      setDisabledInPim(!PricehelperResponseData?.summary?.IsAvailableForPurchase)
    }
  }, [PricehelperResponseData?.summary])

  // isAvailableForPurchase reflects whether the configurator itself is published/buyable in LFC
  const isAvailableForPurchase =
    configResult && isValidJson(configResult)
      ? JSON.parse(configResult)?.isAvailableForPurchase
      : undefined
  if (isAvailableForPurchase !== undefined) {
    setDisabledInLfc(!isAvailableForPurchase)
  }

  useEffect(() => {
    if (disabledInPim) console.log('Article is set as not buyable in pim backend')
    if (disabledInLfc) console.log('Article is set as not buyable in lfc backend')

    ProductPriceBlock?.setAttribute(
      'available-for-purchase',
      disabledInPim || disabledInLfc ? 'false' : 'true'
    )
  }, [disabledInPim, disabledInLfc])

  useEffect(() => {
    const controllerElement: HTMLElement | null = document.getElementById(
      'productPriceBlockController'
    )
    const apiUrl = controllerElement?.getAttribute('skbv-api-base-url') || null
    if (!apiUrl) throw new Error('apiUrl is not defined in controller attributes')

    const getPartPaymentParameters = async () => {
      try {
        const partPaymentProviderApiUrl = `${apiUrl}/Payment/PartPaymentProviderWidget`

        const response = await fetch(`${partPaymentProviderApiUrl}?channelId=${channelId}`, {
          method: 'GET',
        })
        if (!response.ok) {
          throw new Error(`Response status: ${response.status}`)
        }
        return await response.json()
      } catch (error: any) {
        console.error(error.message)
        return null
      }
    }

    const fetchPartPaymentParameters = async () => {
      const parameters = await getPartPaymentParameters()
      setPartPaymentParameters(parameters)
    }

    fetchPartPaymentParameters()
  }, [channelId])

  useEffect(() => {
    if (PricehelperResponseData?.summary && ProductPriceBlock) {
      const event = new CustomEvent('product-price-block-received-pricehelper-summary', {
        bubbles: true,
        cancelable: true,
        detail: PricehelperResponseData?.summary,
      })

      ProductPriceBlock.dispatchEvent(event)
    }
  }, [PricehelperResponseData?.summary, ProductPriceBlock])

  const collectorWidgetContainerRef = useRef<HTMLDivElement | null>(null)
  const collectorWidgetScriptRef = useRef<HTMLScriptElement | null>(null)

  useEffect(() => {
    if (!ProductPriceBlock || !partPaymentParameters) return

    const widgetContainer = document.createElement('div')
    widgetContainer.id = 'collector-widget-container'
    widgetContainer.setAttribute('style', 'width: 92vw; max-width: 400px')
    ProductPriceBlock.appendChild(widgetContainer)
    widgetContainer.style.display = 'none'
    collectorWidgetContainerRef.current = widgetContainer

    const cleanup = () => {
      collectorWidgetScriptRef.current?.remove()
      collectorWidgetScriptRef.current = null
      widgetContainer.remove()
      collectorWidgetContainerRef.current = null
    }

    const { url, token }: PartPaymentParameters = partPaymentParameters

    if (!url || !token) return cleanup

    const script = document.createElement('script') as HTMLScriptElement
    script.id = 'collector-widget-script'
    script.src = `${url}/collector-checkout-loader.js`
    script.dataset.token = token
    script.dataset.widget = 'part-payment'
    script.dataset.containerId = 'collector-widget-container'

    const getLanguage = (channelId: string) => {
      switch (channelId) {
        case '1':
        case '2':
          return 'sv-SE'
        case '4':
        case '9':
          return 'nb-NO'
        case '7':
          return 'sv-FI'
        case '8':
          return 'fi-FI'
        default:
          return ''
      }
    }
    const language = getLanguage(channelId)
    if (language.length) {
      script.dataset.lang = language
    }

    collectorWidgetScriptRef.current = script
    document.body.appendChild(script)

    return cleanup
  }, [ProductPriceBlock, partPaymentParameters])

  useEffect(() => {
    const price = PricehelperResponseData?.summary?.Price
    const widgetContainer = collectorWidgetContainerRef.current
    if (!price || !partPaymentParameters || !widgetContainer) return

    const { minPayment, maxPayment }: PartPaymentParameters = partPaymentParameters
    if (price * 100 >= minPayment && price * 100 <= maxPayment) {
      widgetContainer.style.display = 'block'
      const script = collectorWidgetScriptRef.current
      if (!script) return

      script.dataset.amount = (price * 100).toString()

      window?.walley?.checkout?.api?.update()
    } else {
      widgetContainer.style.display = 'none'
    }
  }, [PricehelperResponseData?.summary?.Price, partPaymentParameters, ProductPriceBlock])

  useEffect(() => {
    if (PricehelperResponseData) {
      const script = generateGtmScriptNode(
        PricehelperResponseData,
        articleErpItemNumber,
        category,
        subCategory,
        weeksUntilDelivery,
        title,
        titleLong
      )
      if (script) {
        const existingScript = document.getElementById(PRODUCT_VIEW_SCRIPT_ID)
        if (existingScript) {
          document.body.removeChild(existingScript)
        }
        document.body.appendChild(script)
      } else {
        throw new Error('Failed to generate GTM script for productView')
      }
    }
  }, [PricehelperResponseData])

  const loaderElement = document.getElementById('productPriceBlockSkeletonLoader')
  if (PricehelperResponseData) {
    loaderElement?.setAttribute('style', 'display: none')
  } else {
    if (loaderElement?.hasAttribute('style')) {
      loaderElement.removeAttribute('style')
    }
  }

  return null
}

export default ProductPriceBlockController
